/**
 * Testimonial Lightbox
 * Lightweight, accessible lightbox for testimonials
 */

import './frontend.css';

(function() {
    'use strict';

    // Store testimonial data
    let currentIndex = 0;
    let testimonialData = [];
    let lightboxElement = null;

    /**
     * Initialize lightbox for all testimonial blocks on the page
     */
    function init() {
        const testimonialBlocks = document.querySelectorAll('.testimonial-block-wrapper');
        if (testimonialBlocks.length === 0) return;

        testimonialBlocks.forEach((block) => {
            // Collect all testimonial data from this block
            const carousel = block.querySelector('.testimonial-carousel');
            if (!carousel) return;

            const slides = carousel.querySelectorAll('.testimonial-slide');
            const blockTestimonials = [];

            slides.forEach((slide) => {
                const rating = slide.dataset.rating || '5';
                const content = slide.dataset.content || '';
                const author = slide.dataset.author || '';
                const testimonialId = slide.dataset.testimonialId || '';

                blockTestimonials.push({
                    rating: parseInt(rating, 10),
                    content: content,
                    author: author,
                    id: testimonialId
                });
            });

            // Wire up per-slide "Read all" trigger + truncation detection for the ACTIVE slide
            const isTextTruncated = (textEl) => {
                if (!textEl) return false;

                // Get computed styles to check if line-clamp is applied
                const computedStyle = window.getComputedStyle(textEl);
                const lineClamp = computedStyle.webkitLineClamp || computedStyle.lineClamp;
                
                // If line-clamp is not set or is 'none', text is not truncated
                if (!lineClamp || lineClamp === 'none') return false;

                // Line-clamp elements can report scrollHeight == clientHeight, so we measure by cloning
                const rect = textEl.getBoundingClientRect();
                if (!rect.width || rect.width === 0) {
                    // Wait for layout
                    return false;
                }

                // Clone the element without line-clamp to measure full height
                const clone = textEl.cloneNode(true);
                clone.style.position = 'absolute';
                clone.style.visibility = 'hidden';
                clone.style.pointerEvents = 'none';
                clone.style.height = 'auto';
                clone.style.maxHeight = 'none';
                clone.style.overflow = 'visible';
                clone.style.webkitLineClamp = 'unset';
                clone.style.lineClamp = 'unset';
                clone.style.display = 'block';
                clone.style.width = `${rect.width}px`;
                clone.style.fontSize = computedStyle.fontSize;
                clone.style.fontFamily = computedStyle.fontFamily;
                clone.style.fontWeight = computedStyle.fontWeight;
                clone.style.lineHeight = computedStyle.lineHeight;
                clone.style.padding = computedStyle.padding;
                clone.style.margin = computedStyle.margin;
                clone.style.boxSizing = computedStyle.boxSizing;

                document.body.appendChild(clone);
                const fullHeight = clone.getBoundingClientRect().height;
                document.body.removeChild(clone);

                const visibleHeight = textEl.getBoundingClientRect().height;
                // Add a small threshold (2px) to account for rounding differences
                const isTruncated = fullHeight > visibleHeight + 2;
                
                return isTruncated;
            };

            const updateActiveTruncation = () => {
                const activeSlide = block.querySelector('.testimonial-slide.active') || block.querySelector('.testimonial-slide');
                if (!activeSlide) return;

                const textEl = activeSlide.querySelector('.testimonial-text');
                const readAllBtn = activeSlide.querySelector('.testimonial-readall');
                if (!textEl || !readAllBtn) return;

                // Reset
                activeSlide.classList.remove('is-truncated');

                // Check if text is actually truncated
                const isTruncated = isTextTruncated(textEl);
                
                if (isTruncated) {
                    activeSlide.classList.add('is-truncated');
                }
            };

            // Clicking "Read all" opens the lightbox starting at that slide
            block.addEventListener('click', (e) => {
                const btn = e.target && e.target.closest ? e.target.closest('.testimonial-readall') : null;
                if (!btn) return;
                e.preventDefault();

                const idx = parseInt(btn.dataset.slideIndex || '0', 10);
                testimonialData = blockTestimonials;
                currentIndex = Number.isFinite(idx) ? idx : 0;
                openLightbox();
            });

            // Update truncation when navigating slides (carousel buttons are inside the block)
            const prevBtn = block.querySelector('.testimonial-prev');
            const nextBtn = block.querySelector('.testimonial-next');
            if (prevBtn) {
                prevBtn.addEventListener('click', () => {
                    window.requestAnimationFrame(updateActiveTruncation);
                });
            }
            if (nextBtn) {
                nextBtn.addEventListener('click', () => {
                    window.requestAnimationFrame(updateActiveTruncation);
                });
            }

            // Initial pass (after layout)
            window.requestAnimationFrame(updateActiveTruncation);

            // Keep in sync on resize (responsive line wrapping affects clamp)
            window.addEventListener('resize', () => window.requestAnimationFrame(updateActiveTruncation), { passive: true });
        });

        // Create lightbox element once
        createLightbox();

        // Global keyboard events
        document.addEventListener('keydown', handleKeydown);
    }

    /**
     * Create the lightbox DOM structure
     */
    function createLightbox() {
        if (document.getElementById('testimonial-lightbox')) return;

        const lightbox = document.createElement('div');
        lightbox.id = 'testimonial-lightbox';
        lightbox.className = 'testimonial-lightbox';
        lightbox.setAttribute('role', 'dialog');
        lightbox.setAttribute('aria-modal', 'true');
        lightbox.setAttribute('aria-label', 'Testimonials lightbox');
        lightbox.innerHTML = `
            <div class="lightbox-overlay" aria-hidden="true"></div>
            <button class="lightbox-close" aria-label="Close lightbox">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </button>
            <button class="lightbox-nav lightbox-prev" aria-label="Previous testimonial">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
            </button>
            <button class="lightbox-nav lightbox-next" aria-label="Next testimonial">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
            </button>
            <div class="lightbox-content">
                <div class="testimonial-lightbox-inner">
                    <div class="testimonial-lightbox-stars"></div>
                    <blockquote class="testimonial-lightbox-quote"></blockquote>
                    <cite class="testimonial-lightbox-author"></cite>
                </div>
            </div>
            <div class="lightbox-counter" aria-live="polite"></div>
        `;

        document.body.appendChild(lightbox);
        lightboxElement = lightbox;

        // Event listeners
        lightbox.querySelector('.lightbox-overlay').addEventListener('click', closeLightbox);
        lightbox.querySelector('.lightbox-close').addEventListener('click', closeLightbox);
        lightbox.querySelector('.lightbox-prev').addEventListener('click', showPrev);
        lightbox.querySelector('.lightbox-next').addEventListener('click', showNext);
    }

    /**
     * Open lightbox
     */
    function openLightbox() {
        if (testimonialData.length === 0) return;

        document.body.classList.add('lightbox-open');
        lightboxElement.classList.add('is-open');
        
        updateLightboxContent();
        
        // Focus management
        lightboxElement.querySelector('.lightbox-close').focus();
    }

    /**
     * Close the lightbox
     */
    function closeLightbox() {
        document.body.classList.remove('lightbox-open');
        lightboxElement.classList.remove('is-open');
    }

    /**
     * Show previous testimonial
     */
    function showPrev() {
        currentIndex = (currentIndex - 1 + testimonialData.length) % testimonialData.length;
        updateLightboxContent();
    }

    /**
     * Show next testimonial
     */
    function showNext() {
        currentIndex = (currentIndex + 1) % testimonialData.length;
        updateLightboxContent();
    }

    /**
     * Update the lightbox content
     */
    function updateLightboxContent() {
        const testimonial = testimonialData[currentIndex];
        if (!testimonial) return;

        const starsContainer = lightboxElement.querySelector('.testimonial-lightbox-stars');
        const quoteElement = lightboxElement.querySelector('.testimonial-lightbox-quote');
        const authorElement = lightboxElement.querySelector('.testimonial-lightbox-author');
        const counter = lightboxElement.querySelector('.lightbox-counter');

        // Render stars
        const themeUri = window.testimonialThemeUri || '';
        const starFiles = ['200_19.svg', '200_21.svg', '200_23.svg', '200_25.svg', '200_27.svg'];
        const rating = testimonial.rating || 5;
        
        starsContainer.innerHTML = '';
        for (let i = 0; i < 5; i++) {
            const starIndex = i < rating ? i : 4;
            const starImg = document.createElement('img');
            starImg.src = themeUri + '/assets/' + starFiles[starIndex];
            starImg.alt = 'star';
            starImg.className = 'w-8 h-8';
            starImg.width = 32;
            starImg.height = 32;
            starsContainer.appendChild(starImg);
        }

        // Update quote
        quoteElement.textContent = `"${testimonial.content}"`;

        // Update author
        authorElement.textContent = testimonial.author;

        // Update counter
        counter.textContent = `${currentIndex + 1} / ${testimonialData.length}`;

        // Update nav visibility
        const prevBtn = lightboxElement.querySelector('.lightbox-prev');
        const nextBtn = lightboxElement.querySelector('.lightbox-next');
        
        if (testimonialData.length > 1) {
            prevBtn.classList.remove('hidden');
            nextBtn.classList.remove('hidden');
        } else {
            prevBtn.classList.add('hidden');
            nextBtn.classList.add('hidden');
        }
    }

    /**
     * Handle keyboard navigation
     */
    function handleKeydown(e) {
        if (!lightboxElement || !lightboxElement.classList.contains('is-open')) return;

        switch (e.key) {
            case 'Escape':
                closeLightbox();
                break;
            case 'ArrowLeft':
                showPrev();
                break;
            case 'ArrowRight':
                showNext();
                break;
        }
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();

